import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
    Box,
    Card,
    Grid,
    Stack,
    Typography,
    Button,
    Divider,
    Alert,
    CircularProgress,
    useTheme
} from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';

import PageContainer from '../../../components/container/PageContainer';
import CustomTextField from '../../../components/forms/theme-elements/CustomTextField';
import apiClient from '../../api/apiClient';
import { useAuthStore, useCampStore } from '../../stores';
import { useSnackbar } from 'notistack';

const OrgInvitePage = () => {
    const navigate = useNavigate();
    const { token } = useParams();
    const { enqueueSnackbar } = useSnackbar();
    const theme = useTheme();
    const setAuthToken = useAuthStore((state) => state.setToken);
    const setCamp = useCampStore((state) => state.setCamp);

    const [campInfo, setCampInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const [formData, setFormData] = useState({
        username: '',
        password: '',
        name: '',
        surname: ''
    });

    useEffect(() => {
        const fetchCampInfo = async () => {
            try {
                const response = await apiClient.get(`/api/camps/public/by-invite/${token}`);
                setCampInfo(response.data);
            } catch (err) {
                if (err.response?.status === 404) {
                    setError('Ссылка приглашения недействительна или устарела');
                } else {
                    setError('Ошибка при загрузке информации о лагере');
                }
            } finally {
                setLoading(false);
            }
        };

        if (token) {
            fetchCampInfo();
        } else {
            setError('Отсутствует токен приглашения');
            setLoading(false);
        }
    }, [token]);

    const handleChange = (field) => (e) => {
        setFormData(prev => ({ ...prev, [field]: e.target.value }));
    };

    const isFormValid = () => {
        return formData.username.trim().length >= 3 &&
            formData.password.length >= 6 &&
            formData.name.trim() &&
            formData.surname.trim();
    };

    const handleSubmit = async () => {
        if (!isFormValid()) return;

        setSubmitting(true);
        try {
            await apiClient.post('/api/camps/public/registration/org', {
                username: formData.username.trim(),
                password: formData.password,
                orgInviteToken: token,
                name: formData.name.trim(),
                surname: formData.surname.trim()
            });

            const loginResponse = await apiClient.post('/api/users/public/auth/login', {
                username: formData.username.trim(),
                password: formData.password
            });

            setAuthToken(loginResponse.data.accessToken);
            sessionStorage.setItem('userId', loginResponse.data.userId);
            sessionStorage.setItem('refreshToken', loginResponse.data.refreshToken);

            if (campInfo) {
                setCamp({
                    campId: campInfo.campId,
                    campName: campInfo.name
                });
            }

            enqueueSnackbar('Регистрация успешна! Добро пожаловать в команду организаторов.', { variant: 'success' });
            navigate('/');
        } catch (error) {
            const message = error.response?.data?.message || 'Ошибка при регистрации';
            enqueueSnackbar(message, { variant: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <PageContainer title="Загрузка...">
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '100vh'
                    }}
                >
                    <CircularProgress />
                </Box>
            </PageContainer>
        );
    }

    if (error) {
        return (
            <PageContainer title="Ошибка">
                <Box
                    sx={{
                        position: 'relative',
                        minHeight: '100vh',
                        '&:before': {
                            content: '""',
                            background: theme.palette.background.auth,
                            backgroundSize: '400% 400%',
                            animation: 'gradient 15s ease infinite',
                            position: 'absolute',
                            height: '100%',
                            width: '100%',
                            opacity: '0.3',
                        },
                    }}
                >
                    <Grid container spacing={0} justifyContent="center" sx={{ height: '100vh' }}>
                        <Grid
                            item
                            xs={12}
                            sm={10}
                            md={6}
                            lg={4}
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                        >
                            <Card elevation={9} sx={{ p: 4, zIndex: 1, width: '100%' }}>
                                <Stack spacing={3} alignItems="center">
                                    <ErrorIcon color="error" sx={{ fontSize: theme.typography.iconSizes.xxl }} />
                                    <Typography variant="h5" textAlign="center">
                                        {error}
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        onClick={() => navigate('/login')}
                                    >
                                        Перейти к входу
                                    </Button>
                                </Stack>
                            </Card>
                        </Grid>
                    </Grid>
                </Box>
            </PageContainer>
        );
    }

    return (
        <PageContainer title="Регистрация организатора">
            <Box
                sx={{
                    position: 'relative',
                    minHeight: '100vh',
                    '&:before': {
                        content: '""',
                        background: theme.palette.background.auth,
                        backgroundSize: '400% 400%',
                        animation: 'gradient 15s ease infinite',
                        position: 'absolute',
                        height: '100%',
                        width: '100%',
                        opacity: '0.3',
                    },
                }}
            >
                <Grid container spacing={0} justifyContent="center" sx={{ py: 4 }}>
                    <Grid item xs={12} sm={10} md={8} lg={5}>
                        <Card elevation={9} sx={{ p: 4, zIndex: 1 }}>
                            <Stack spacing={3}>
                                <Box textAlign="center">
                                    <Typography variant="h4">
                                        Присоединиться как организатор
                                    </Typography>
                                    <Typography variant="body1" color="primary" mt={1}>
                                        Лагерь: {campInfo?.name}
                                    </Typography>
                                </Box>

                                <Alert severity="info">
                                    Вы приглашены стать организатором лагеря. Заполните форму для регистрации.
                                </Alert>

                                <Divider />

                                <CustomTextField
                                    label="Логин"
                                    placeholder="Минимум 3 символа"
                                    fullWidth
                                    value={formData.username}
                                    onChange={handleChange('username')}
                                />

                                <CustomTextField
                                    label="Пароль"
                                    type="password"
                                    placeholder="Минимум 6 символов"
                                    fullWidth
                                    value={formData.password}
                                    onChange={handleChange('password')}
                                />

                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                    <CustomTextField
                                        label="Имя"
                                        fullWidth
                                        value={formData.name}
                                        onChange={handleChange('name')}
                                    />
                                    <CustomTextField
                                        label="Фамилия"
                                        fullWidth
                                        value={formData.surname}
                                        onChange={handleChange('surname')}
                                    />
                                </Stack>

                                <Button
                                    variant="contained"
                                    size="large"
                                    fullWidth
                                    onClick={handleSubmit}
                                    disabled={!isFormValid() || submitting}
                                >
                                    {submitting ? <CircularProgress size={24} /> : 'Зарегистрироваться'}
                                </Button>

                                <Box textAlign="center">
                                    <Typography variant="body2" color="text.secondary" component="span">
                                        Уже есть аккаунт?
                                    </Typography>
                                    <Button
                                        variant="text"
                                        size="small"
                                        onClick={() => navigate('/login')}
                                        sx={{ ml: 0.5, textTransform: 'none', typography: 'body2', p: 0, minWidth: 'auto' }}
                                    >
                                        Войти
                                    </Button>
                                </Box>
                            </Stack>
                        </Card>
                    </Grid>
                </Grid>
            </Box>
        </PageContainer>
    );
};

export default OrgInvitePage;
